import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useWorkOrderList } from '@modules/planner/planner-api';
import {
  getNextWorkOrder,
  getPreviousWorkOrder,
} from '@composite/calendar/utils';
import { WorkOrder } from '@models';
import { CalendarDimensions } from '@enums';

type ReturnPromise = {
  previousWorkOrder?: WorkOrder;
  nextWorkOrder?: WorkOrder;
};

export function useFetchEdgeWorkOrders(workOrder: WorkOrder) {
  const [skip, setSkip] = useState(true);
  const { loading, result, setLoading } = useWorkOrderList({
    mobileUnits: workOrder.mobileUnit ? [workOrder.mobileUnit] : [],
    technicians: workOrder.user ? [workOrder.user] : [],
    currentDay: dayjs(workOrder.startTime).startOf('day').unix(),
    dimension: CalendarDimensions.DAY,
    skip,
  });
  const promiseRef = useRef<{
    resolve: ({ previousWorkOrder, nextWorkOrder }: ReturnPromise) => void;
    reject: (reason?: any) => void;
  } | null>(null);

  useEffect(() => {
    if (!loading && !skip && promiseRef.current) {
      if (result?.items) {
        const previousWorkOrder = getPreviousWorkOrder(
          [workOrder, ...result.items],
          dayjs(workOrder.startTime).unix()
        );

        const nextWorkOrder = getNextWorkOrder(
          [workOrder, ...result.items],
          dayjs(workOrder.startTime).unix()
        );
        promiseRef.current.resolve({ previousWorkOrder, nextWorkOrder });
      } else {
        promiseRef.current.resolve({
          previousWorkOrder: undefined,
          nextWorkOrder: undefined,
        });
      }
      promiseRef.current = null;
    }
  }, [loading, skip, result, workOrder]);

  const fetchEdgeWorkOrders = useCallback(() => {
    return new Promise<ReturnPromise>((resolve, reject) => {
      setSkip(false);
      setLoading(true);
      promiseRef.current = { resolve, reject };
    });
  }, [setLoading]);

  return {
    fetchEdgeWorkOrders,
  };
}
